import dayjs from 'dayjs';

export const FORM_DRAFT_PREFIX = 'react-admin.form.v1.';

const LEGACY_FORM_DRAFT_PREFIX = 'react-admin.form.';
const DAYJS_MARKER = '__reactAdminDayjs';

type EncodedValue =
  | null
  | string
  | number
  | boolean
  | EncodedValue[]
  | { [key: string]: EncodedValue };

function getSessionStorage() {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function encodeValue(value: unknown): EncodedValue | undefined {
  if (dayjs.isDayjs(value)) {
    return { [DAYJS_MARKER]: value.toISOString() };
  }

  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => encodeValue(item) ?? null);
  }

  if (typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(
      Object.entries(value).flatMap(([key, item]) => {
        const encoded = encodeValue(item);
        return encoded === undefined ? [] : [[key, encoded]];
      }),
    );
  }

  return undefined;
}

function decodeValue(value: EncodedValue): unknown {
  if (Array.isArray(value)) {
    return value.map(decodeValue);
  }

  if (value && typeof value === 'object') {
    if (Object.keys(value).length === 1 && typeof value[DAYJS_MARKER] === 'string') {
      const restored = dayjs(value[DAYJS_MARKER]);
      return restored.isValid() ? restored : undefined;
    }

    return Object.fromEntries(
      Object.entries(value).flatMap(([key, item]) => {
        const decoded = decodeValue(item);
        return decoded === undefined ? [] : [[key, decoded]];
      }),
    );
  }

  return value;
}

function keyForScope(scope: string) {
  return `${FORM_DRAFT_PREFIX}${scope}`;
}

export function createFormDraftScope(routeId: string, surfaceId: string) {
  return `${routeId}.${surfaceId}`;
}

export function cloneFormDraftValue<T>(value: T): T {
  const encoded = encodeValue(value);
  if (encoded === undefined) {
    return value;
  }
  return decodeValue(encoded) as T;
}

export function mergeFormDraftValue<T>(defaults: T, draft: T): T {
  if (
    defaults &&
    draft &&
    typeof defaults === 'object' &&
    typeof draft === 'object' &&
    !Array.isArray(defaults) &&
    !Array.isArray(draft) &&
    !dayjs.isDayjs(defaults) &&
    !dayjs.isDayjs(draft)
  ) {
    const defaultRecord = defaults as Record<string, unknown>;
    const draftRecord = draft as Record<string, unknown>;
    const merged = { ...defaultRecord };

    for (const [key, value] of Object.entries(draftRecord)) {
      merged[key] =
        key in defaultRecord
          ? mergeFormDraftValue(defaultRecord[key], value)
          : cloneFormDraftValue(value);
    }

    return merged as T;
  }

  return cloneFormDraftValue(draft);
}

export function readFormDraft<T>(scope: string): T | undefined {
  const storage = getSessionStorage();
  const key = keyForScope(scope);
  if (!storage) {
    return undefined;
  }

  try {
    const raw = storage.getItem(key);
    if (!raw) {
      return undefined;
    }
    return decodeValue(JSON.parse(raw) as EncodedValue) as T;
  } catch {
    try {
      storage.removeItem(key);
    } catch {
      // Storage is optional. A rejected cleanup must not block page rendering.
    }
    return undefined;
  }
}

export function writeFormDraft<T>(scope: string, value: T) {
  const storage = getSessionStorage();
  const encoded = encodeValue(value);
  if (!storage || encoded === undefined) {
    return;
  }

  try {
    storage.setItem(keyForScope(scope), JSON.stringify(encoded));
  } catch {
    // Quota and privacy-mode failures degrade to a non-persistent form.
  }
}

export function clearFormDraft(scope: string) {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(keyForScope(scope));
  } catch {
    // Storage cleanup is best effort.
  }
}

export function clearRouteDrafts(routeId: string) {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  const routePrefix = keyForScope(`${routeId}.`);
  try {
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (key?.startsWith(routePrefix)) {
        storage.removeItem(key);
      }
    }
  } catch {
    // Storage cleanup is best effort.
  }
}

export function clearAllFormDrafts() {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  try {
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (key?.startsWith(LEGACY_FORM_DRAFT_PREFIX)) {
        storage.removeItem(key);
      }
    }
  } catch {
    // Storage cleanup is best effort.
  }
}
